// Import necessary React hooks
import { useState } from "react";
// Import necessary Material-UI components
import {
  TextField,
  Grid,
  Typography,
  Box,
  Button,
  Container,
} from "@mui/material";
// Import custom validation function and hooks
import { validateRequestInput } from "../validation/validateRequestInput.js";
import { useNewRequest, useNewRequestContext } from "../hooks";
// Import common utilities and data
import {
  useFormDropdownFields,
  CustomMUIDatePickerWithErrors,
} from "../../common";
import _ from "lodash";
import {
  HODDepartmentSelect,
  ExecutiveChefEmailSelect,
  BusinessUnitSelect,
  FormDetailsSelect,
} from "../common";

export function SampleRequestDetailsForm({ decoded }) {
  console.log("sample request details form");
  // State for storing form errors
  const [errors, setErrors] = useState();
  // Fetching management list data
  const {
    executiveChefList = [],
    hodList = [],
    endUserLocations = [],
    buNumbers = [],
  } = useFormDropdownFields();
  // Hooks for managing sample request details and form display
  const { setSampleRequestDetails, setSampleRequestFormDisplay } =
    useNewRequest();
  const { state } = useNewRequestContext();

  // State for storing form dropdown Select values
  const [values, setValues] = useState({
    HODDepartmentSelect: "",
    executiveChefEmailSelect: "",
    purposeSelect: "",
    endUserLocationSelect: "",
    businessUnitSelect: "",
  });
  // Handler for selecting values from dropdown Selects
  const handleSelect = (id, value) => {
    setValues({ ...values, [id]: value });
  };

  // Handler for setting data and navigating to the next part
  const handleSetData = (data) => {
    setSampleRequestDetails({ ...data });
    window.scrollTo(0, 0);
    setSampleRequestFormDisplay("none");
  };

  // Handler for form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    // Prepare list of approver emails for validation -- to check chef added is not and executive chef or hod
    const approversEmailList = [
      ...executiveChefList.map((item) => {
        return item.email;
      }),
      ...hodList.map((item) => {
        return item.email;
      }),
    ];

    // Collect form data
    const data = new FormData(event.currentTarget);

    // Prepare sample details object
    const sampleDetails = {
      fullName: data.get("fullName"),
      department: data.get("department"),
      email:
        decoded.role === "procurement"
          ? data.get("email").toLowerCase()
          : decoded.email,
      purpose: values.purposeSelect,
      endUserLocation: values.endUserLocationSelect,
      executiveChefEmail:
        decoded?.role === "executiveChef"
          ? decoded.email
          : values.executiveChefEmailSelect,
      HODDepartment: values.HODDepartmentSelect,
      organization: data.get("organization"),
      needByDate: data.get("needByDate").replace(/ /g, ""),
      businessUnit: values.businessUnitSelect,
      items: [],
    };

    // Validate form inputs
    const { validationErrors, isValid } = validateRequestInput(
      sampleDetails,
      approversEmailList,
      decoded.role
    );
    !isValid && setErrors(validationErrors);
    // Set data to state
    isValid && handleSetData(sampleDetails);
  };

  // Log errors if any
  errors && console.log({ errors });

  // Render the form
  return (
    <div className="card" style={{ display: state.sampleRequestFormDisplay }}>
      <div
        className="card__text"
        style={{
          padding: "20px 20px",
          overflowWrap: "break-word",
        }}
      >
        <Grid item xs={12} mb={3}>
          <Typography component="h1" variant="h6">
            Fill out the request details below and click on next to fill out
            sample details.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            <b>Request Details</b>
          </Typography>
        </Grid>
        <Box
          sx={{
            marginTop: 5,
          }}
        >
          <Container component="main">
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Grid
                container
                spacing={2}
                direction="row"
                alignItems="flex-start"
              >
                <Grid container item xs={12} md={6} spacing={2}>
                  {/* Render pre-filled disabled text fields for request date, requester full name, and requester department */}
                  {[
                    { name: "date", value: new Date().toDateString() },
                    {
                      name: "fullName",
                      value: decoded.firstName + " " + decoded.lastName,
                    },
                    { name: "organization", value: decoded.organization },
                    {
                      name: "department",
                      value: decoded.department,
                    },
                  ].map((field, index) => {
                    return (
                      <Grid item xs={12} sm={12} key={index}>
                        <TextField
                          variant="filled"
                          name={field.name}
                          required
                          fullWidth
                          value={field.value}
                          id={field.name}
                          label={_.startCase(field.name)}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
                <Grid container item xs={12} md={6} spacing={2}>
                  {/* Conditionally render text field for chef email if the user role is procurement */}
                  {decoded.role === "procurement" && (
                    <Grid item xs={12} sm={12}>
                      <TextField
                        name="email"
                        required
                        fullWidth
                        id="email"
                        label="Chef Email"
                        error={errors && errors.email && true}
                        helperText={
                          errors && errors.email !== "" ? errors.email : ""
                        }
                      />
                    </Grid>
                  )}

                  {/* Render select inputs for purpose and end user location */}
                  {[
                    {
                      id: "purpose",
                      options: [
                        "Alternative product",
                        "New Item",
                        "Tender",
                        "Presentation",
                      ],
                    },
                    { id: "endUserLocation", options: endUserLocations },
                    // { id: "businessUnit", options: buNumbers },
                  ].map((item, index) => {
                    return (
                      <Grid item xs={12} sm={12} key={index}>
                        <FormDetailsSelect
                          item={item}
                          errors={errors}
                          handleSelect={handleSelect}
                        />
                      </Grid>
                    );
                  })}
                  {/* Conditionally render text field for selecting business unit */}
                  <Grid item xs={12} sm={12}>
                    <BusinessUnitSelect
                      handleSelect={handleSelect}
                      buNumbers={buNumbers}
                      errors={errors}
                    />
                  </Grid>

                  {/* Conditionally render text field for executive chef email based on user role */}
                  {decoded?.role === "executiveChef" ? (
                    <Grid item xs={12} sm={12}>
                      <TextField
                        disabled={true}
                        name="executiveChefEmail"
                        value={decoded.email}
                        label="Executive Chef Email"
                        variant="filled"
                        required
                        fullWidth
                        id="executiveChefEmail"
                        error={errors && errors.fullName && true}
                        helperText={
                          errors && errors.fullName !== ""
                            ? errors.fullName
                            : ""
                        }
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={12}>
                      <ExecutiveChefEmailSelect
                        handleSelect={handleSelect}
                        executiveChefList={executiveChefList}
                        errors={errors}
                      />
                    </Grid>
                  )}

                  {/* Render autocomplete for HOD department selection */}
                  <Grid item xs={12} sm={12}>
                    <HODDepartmentSelect
                      handleSelect={handleSelect}
                      hodList={hodList}
                      errors={errors}
                    />
                  </Grid>

                  {/* Render DatePicker for Form */}
                  <Grid item xs={12} sm={12}>
                    <CustomMUIDatePickerWithErrors
                      name={"needByDate"}
                      errors={errors}
                      shouldDisableWeekends={true}
                      shouldHaveMinDate={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 6,
                  float: "right",
                }}
              >
                Next
              </Button>
            </Box>
          </Container>
        </Box>
      </div>
    </div>
  );
}
